/** Served from `public/product-assets/<slug>/` — replace placeholders with PNG/JPEG from App Store Connect. */
export const productAsset = (slug: string, filename: string) =>
  `/product-assets/${slug}/${filename}`;

export type Product = {
  slug: string;
  name: string;
  tagline: string;
  category: string;
  appStoreUrl: string;
  /** App icon — typically 1024×1024 master; displayed small (use PNG or SVG). */
  iconSrc: string;
  /** Phone screenshots — portrait 1290×2796 or similar; 2–5 images typical. */
  screenshotSrcs: string[];
  /** Intro / description — may be multi-line; product page preserves line breaks. */
  summary: string;
  /** Key bullets for the product page */
  highlights: string[];
};

export const PRODUCTS: Product[] = [
  {
    slug: 'safe-food-scan',
    name: 'Safe Food Scan',
    tagline: 'Allergen scanner for food',
    category: 'Health & Fitness',
    appStoreUrl: 'https://apps.apple.com/sa/app/safe-food-scan/id6758021373',
    iconSrc: productAsset('safe-food-scan', 'icon.png'),
    screenshotSrcs: [
      productAsset('safe-food-scan', 'screenshot-1.png'),
      productAsset('safe-food-scan', 'screenshot-2.png'),
      productAsset('safe-food-scan', 'screenshot-3.png'),
      productAsset('safe-food-scan', 'screenshot-4.png'),
    ],
    summary: `Safe Food Scan helps people with food allergies quickly check if products are safe to eat. Simply scan a barcode to instantly analyze ingredients and get a clear safety status.

INSTANT ALLERGEN DETECTION
• Scan food product barcodes with your camera
• Get immediate Safe / Caution / Not Safe status
• Detect allergens in ingredients and trace warnings
• Manual barcode entry when camera isn't available

15 BUILT-IN ALLERGENS
• Milk, Eggs, Peanuts, Tree Nuts, Wheat
• Gluten, Soy, Fish, Shellfish, Sesame
• Mustard, Celery, Lupin, Molluscs, Sulfites
• Each allergen includes comprehensive keyword detection

FULLY CUSTOMIZABLE
• Create custom allergens with your own keywords
• Choose sensitivity level: Strict or Moderate
• Enable/disable individual allergens anytime
• Quick-add common child allergens with one tap

DETAILED PRODUCT INFO
• View Nutri-Score (A-E) nutrition grades
• See NOVA food processing level (1-4)
• Browse parsed ingredient lists
• Product images and brand information

SCAN HISTORY
• Track all your scanned products
• View safety statistics at a glance
• Quickly reference previous scans
• Delete individual scans or clear history

Safe Food Scan uses the Open Food Facts database with over 3 million products worldwide. Always verify ingredients on the actual product label before consumption.

Take control of your food allergies. Scan smarter, eat safer!`,
    highlights: [
      'Scan product barcodes or enter codes manually when the camera is unavailable',
      'Immediate Safe / Caution / Not Safe status with allergen and trace detection',
      '15 built-in allergens plus custom allergens, keywords, and Strict or Moderate sensitivity',
      'Nutri-Score, NOVA processing levels, parsed ingredients, and scan history',
      'Powered by the Open Food Facts database (millions of products)—always verify on the actual label',
    ],
  },
  {
    slug: 'best-shot-ai-photo-selector',
    name: 'Best Shot — AI Photo Selector',
    tagline: 'Find your best photos with AI',
    category: 'Photo & Video',
    appStoreUrl: 'https://apps.apple.com/sa/app/best-shot-ai-photo-selector/id6757730398',
    iconSrc: productAsset('best-shot-ai-photo-selector', 'icon.png'),
    screenshotSrcs: [
      productAsset('best-shot-ai-photo-selector', 'screenshot-1.png'),
      productAsset('best-shot-ai-photo-selector', 'screenshot-2.png'),
      productAsset('best-shot-ai-photo-selector', 'screenshot-3.png'),
      productAsset('best-shot-ai-photo-selector', 'screenshot-4.png'),
    ],
    summary: `Best Shots AI uses on-device AI to find your best photos from bursts and similar shots. Stop scrolling through dozens of nearly identical pictures – let AI pick the sharpest, best-exposed photos for you.

SMART PHOTO ANALYSIS
• AI analyzes sharpness, exposure, and composition
• Finds the best shots from burst photos
• Groups similar photos together automatically

PRIVACY FIRST
• All processing happens entirely on your device
• Your photos never leave your phone
• No cloud uploads, no data collection

EASY TO USE
• Select photos to scan
• Review AI recommendations
• Delete duplicates with one tap

FREE UP STORAGE
• See exactly how much space you'll save
• Keep only the photos you love
• Clean up your photo library effortlessly

Perfect for anyone who takes burst photos, multiple shots of the same scene, or wants to declutter their photo library while keeping the best memories.`,
    highlights: [
      'Analyzes sharpness, exposure, and composition on your device',
      'Picks the best shots from burst photos and groups similar images',
      'Review AI picks, delete duplicates, and see how much storage you can free',
    ],
  },
  {
    slug: 'gita-way',
    name: 'Gita Way',
    tagline: 'A simple way with Gita',
    category: 'Reference',
    appStoreUrl: 'https://apps.apple.com/sa/app/gita-way/id6757721585',
    iconSrc: productAsset('gita-way', 'icon.png'),
    screenshotSrcs: [
      productAsset('gita-way', 'screenshot-1.png'),
      productAsset('gita-way', 'screenshot-2.png'),
      productAsset('gita-way', 'screenshot-3.png'),
    ],
    summary: `GitaWay — A Companion for Reflection
Read. Reflect. Return.


GitaWay is a thoughtfully designed mobile app for exploring selected verses of the Bhagavad Gita through structured reading and quiet reflection.

The app presents chapters and verses in a clear, distraction-free interface, with transliteration, translation, and meaning available for each verse. In addition to traditional reading, GitaWay offers curated thematic series that group verses around shared inner themes such as clarity, balance, action, and steadiness. These series are designed to be experienced gradually, one verse at a time.

Users can search across chapters and verses to explore ideas of interest, bookmark verses for later reflection, and easily return to previously saved content. Progress within series is preserved, allowing users to continue where they left off.

GitaWay is intended for personal exploration and contemplation. It does not provide advice or instruction, and it is not tied to religious practice. Instead, it offers a calm, app-based experience that encourages thoughtful engagement with timeless text in a modern, accessible way.`,
        highlights: [
      'Clear, distraction-free reading with search across chapters and verses',
      'Bookmark verses and resume thematic series where you left off',
      'Designed for personal reflection—not instruction or religious practice',
    ],
  },
  {
    slug: 'quiziio',
    name: 'Quiziio',
    tagline: 'Test your knowledge today',
    category: 'Entertainment',
    appStoreUrl: 'https://apps.apple.com/sa/app/quiziio/id6757763412',
    iconSrc: productAsset('quiziio', 'icon.png'),
    screenshotSrcs: [
      productAsset('quiziio', 'screenshot-1.png'),
      productAsset('quiziio', 'screenshot-2.png'),
      productAsset('quiziio', 'screenshot-3.png'),
      productAsset('quiziio', 'screenshot-4.png'),
      productAsset('quiziio', 'screenshot-5.png'),
    ],
    summary: `Challenge yourself with Quiziio, the ultimate trivia quiz app! Test your knowledge across a wide variety of categories and difficulty levels with thousands of questions.

ENDLESS TRIVIA FUN
• Access thousands of trivia questions from the Open Trivia Database
• Multiple choice questions with instant feedback
• See correct answers highlighted in real-time
• Track your progress with a visual progress bar

CUSTOMIZE YOUR EXPERIENCE
• Choose from 20+ categories including Science, History, Sports, Entertainment, and more
• Select your difficulty: Easy, Medium, or Hard
• Play with Random categories for a mixed challenge
• Perfect for quick brain workouts or extended sessions

DETAILED RESULTS
• See your score percentage after each quiz
• Get encouraging messages based on your performance
• Review all your answers to learn from mistakes
• Compare correct vs incorrect answers at a glance

BEAUTIFUL DESIGN
• Clean, modern interface
• Automatic dark mode support
• Smooth animations and transitions
• Designed for both iPhone and iPad

Whether you're preparing for trivia night, want to learn something new, or just looking for a fun way to pass the time, Quiziio has you covered. How much do you really know? Find out now!`,
    highlights: [
      '20+ categories and Easy / Medium / Hard—or Random for a mixed challenge',
      'Multiple choice with instant feedback and a visual progress bar',
      'Review answers after each quiz with encouraging score summaries',
      'Clean UI with automatic dark mode, smooth animations, iPhone and iPad',
    ],
  },
];

const bySlug = new Map(PRODUCTS.map((p) => [p.slug, p]));

export function getProductBySlug(slug: string | undefined): Product | undefined {
  if (!slug) return undefined;
  return bySlug.get(slug);
}
